import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../Config/config';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';

// ─── Helpers ────────────────────────────────────────────────────────────────

const fmtDate = (raw) => {
  if (!raw) return '—';
  const d = new Date(raw);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');
};

const fmtNum = (n) => Number(n || 0).toLocaleString('en-IN');

const planLabel = (code) => {
  if (!code) return 'Free';
  const map = {
    TITANIUM_ELITE: 'Enterprise',
    PLATINUM_GROWTH: 'Premium',
    GOLD_LAUNCH: 'Basic',
    FREE: 'Free',
  };
  return map[String(code).toUpperCase()] || code;
};

const PLAN_STYLE = {
  Enterprise: { bg: '#EDE9FE', color: '#5B21B6', border: '#C4B5FD' },
  Premium:    { bg: '#DBEAFE', color: '#1D4ED8', border: '#BFDBFE' },
  Basic:      { bg: '#D1FAE5', color: '#065F46', border: '#A7F3D0' },
  Free:       { bg: '#F3F4F6', color: '#374151', border: '#D1D5DB' },
};

const getSubscriptionStatus = (validTill, planCode) => {
  if (!planCode && !validTill) return { label: 'Inactive', style: { bg: '#F3F4F6', color: '#374151', border: '#D1D5DB' } };
  if (!validTill) return { label: 'Active', style: { bg: '#D1FAE5', color: '#065F46', border: '#A7F3D0' } };
  const now = Date.now();
  const exp = new Date(validTill).getTime();
  const diff = exp - now;
  const days15 = 15 * 24 * 60 * 60 * 1000;
  if (diff < 0) return { label: 'Expired', style: { bg: '#FEE2E2', color: '#991B1B', border: '#FECACA' } };
  if (diff < days15) return { label: 'Near Expiry', style: { bg: '#FEF3C7', color: '#92400E', border: '#FDE68A' } };
  return { label: 'Active', style: { bg: '#D1FAE5', color: '#065F46', border: '#A7F3D0' } };
};

const creditBarColor = (pct) => {
  if (pct >= 90) return '#EF4444';
  if (pct >= 70) return '#F59E0B';
  return '#10B981';
};

// ─── Skeleton ────────────────────────────────────────────────────────────────

const Skeleton = ({ w = '100%', h = 16, r = 6 }) => (
  <div style={{
    width: w, height: h, borderRadius: r,
    background: 'linear-gradient(90deg,#F1F5F9 25%,#E2E8F0 50%,#F1F5F9 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.4s infinite linear',
  }} />
);

// ─── Summary Card ────────────────────────────────────────────────────────────

const SummaryCard = ({ icon, label, value, gradient, iconBg }) => (
  <div style={{
    background: gradient,
    borderRadius: 14,
    padding: '22px 24px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
    display: 'flex', alignItems: 'center', gap: 16,
    transition: 'transform 0.18s, box-shadow 0.18s',
    cursor: 'default',
  }}
    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.12)'; }}
    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.07)'; }}
  >
    <div style={{ width: 48, height: 48, borderRadius: 12, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
      {icon}
    </div>
    <div>
      <div style={{ fontSize: 26, fontWeight: 800, color: '#1E293B', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, color: '#64748B', fontWeight: 600, marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    </div>
  </div>
);

// ─── Filter Select ────────────────────────────────────────────────────────────

const FilterSelect = ({ value, onChange, options, placeholder }) => (
  <select
    value={value}
    onChange={e => onChange(e.target.value)}
    style={{
      height: 40, padding: '0 10px', borderRadius: 8, fontSize: 13, fontWeight: 500,
      border: '1.5px solid #E2E8F0', background: '#fff', color: '#334155', outline: 'none',
      cursor: 'pointer', minWidth: 130,
    }}
  >
    <option value="">{placeholder}</option>
    {options.map(o => <option key={o} value={o}>{o}</option>)}
  </select>
);

// ─── Header Cell ─────────────────────────────────────────────────────────────

const TH = ({ children, style = {} }) => (
  <th style={{
    padding: '13px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700,
    color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em',
    whiteSpace: 'nowrap', background: '#F8FAFC', borderBottom: '2px solid #E2E8F0',
    ...style,
  }}>
    {children}
  </th>
);

// ─── Data Cell ───────────────────────────────────────────────────────────────

const TD = ({ children, style = {} }) => (
  <td style={{
    padding: '13px 14px', fontSize: 13, color: '#334155',
    borderBottom: '1px solid #F1F5F9', verticalAlign: 'middle',
    ...style,
  }}>
    {children}
  </td>
);

// ─── Badge ────────────────────────────────────────────────────────────────────

const Badge = ({ label, bg, color, border }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
    background: bg, color, border: `1.5px solid ${border}`,
    whiteSpace: 'nowrap',
  }}>
    {label}
  </span>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const AdminCollegesDashboard = () => {
  const navigate = useNavigate();
  const [colleges, setColleges]               = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [openDialog, setOpenDialog]           = useState(false);
  const [selectedCollegeId, setSelectedCollegeId] = useState(null);
  const [deleting, setDeleting]               = useState(false);

  // Filter state
  const [searchName,   setSearchName]   = useState('');
  const [searchEmail,  setSearchEmail]  = useState('');
  const [searchUser,   setSearchUser]   = useState('');
  const [searchPhone,  setSearchPhone]  = useState('');
  const [filterPlan,   setFilterPlan]   = useState('');
  const [filterState,  setFilterState]  = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchColleges = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/colleges`);
      setColleges(res.data || []);
    } catch (err) {
      console.error('Error fetching colleges:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchColleges(); }, [fetchColleges]);

  // ── Delete ─────────────────────────────────────────────────────────────────

  const handleOpenDialog    = (id) => { setSelectedCollegeId(id); setOpenDialog(true); };
  const handleCloseDialog   = () => { setOpenDialog(false); setSelectedCollegeId(null); };
  const handleConfirmDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API_BASE_URL}/colleges/delete/${selectedCollegeId}`);
      fetchColleges();
      handleCloseDialog();
    } catch (err) {
      console.error('Error deleting college:', err);
    } finally {
      setDeleting(false);
    }
  };

  // ── Derived filter options ─────────────────────────────────────────────────

  const allPlans   = useMemo(() => [...new Set(colleges.map(c => planLabel(c.active_plan_code)).filter(Boolean))].sort(), [colleges]);
  const allStates  = useMemo(() => [...new Set(colleges.map(c => c.state).filter(Boolean))].sort(), [colleges]);
  const allStatuses = ['Active', 'Near Expiry', 'Expired', 'Inactive'];

  const resetFilters = () => {
    setSearchName(''); setSearchEmail(''); setSearchUser(''); setSearchPhone('');
    setFilterPlan(''); setFilterState(''); setFilterStatus('');
  };
  const hasFilter = searchName || searchEmail || searchUser || searchPhone || filterPlan || filterState || filterStatus;

  // ── Filtered rows ──────────────────────────────────────────────────────────

  const filtered = useMemo(() => colleges.filter(c => {
    const status = getSubscriptionStatus(c.plan_valid_till, c.active_plan_code).label;
    return (
      (!searchName  || c.name?.toLowerCase().includes(searchName.toLowerCase())) &&
      (!searchEmail || c.email?.toLowerCase().includes(searchEmail.toLowerCase())) &&
      (!searchUser  || c.username?.toLowerCase().includes(searchUser.toLowerCase())) &&
      (!searchPhone || String(c.phone || '').includes(searchPhone)) &&
      (!filterPlan  || planLabel(c.active_plan_code) === filterPlan) &&
      (!filterState || c.state === filterState) &&
      (!filterStatus|| status === filterStatus)
    );
  }), [colleges, searchName, searchEmail, searchUser, searchPhone, filterPlan, filterState, filterStatus]);

  // ── Summary stats ──────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    const now = Date.now();
    let active = 0, expired = 0, totalPurchased = 0, totalUsed = 0;
    colleges.forEach(c => {
      const exp = c.plan_valid_till ? new Date(c.plan_valid_till).getTime() : null;
      if (exp && exp > now) active++;
      if (exp && exp <= now) expired++;
      totalPurchased += Number(c.total_credits_purchased || 0);
      totalUsed      += Number(c.credits_used || 0);
    });
    return {
      total: colleges.length,
      active,
      expired,
      totalPurchased,
      totalUsed,
      remaining: totalPurchased - totalUsed,
    };
  }, [colleges]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC', padding: '32px 28px', fontFamily: 'Inter,system-ui,sans-serif' }}>

      {/* Global shimmer keyframe */}
      <style>{`@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>

      {/* ── Header ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>

          {/* Left: icon + title + subtitle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
            <button
              onClick={() => navigate(-1)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 38, height: 38, borderRadius: 10, border: '1.5px solid #E2E8F0',
                background: '#fff', color: '#64748B', cursor: 'pointer',
                transition: 'all 0.15s', flexShrink: 0
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#F1F5F9'; e.currentTarget.style.color = '#0F172A'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#64748B'; }}
              title="Go Back"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
            </button>
            <div style={{ width: 42, height: 42, borderRadius: 11, background: 'linear-gradient(135deg,#6366F1,#8B5CF6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
              🏛️
            </div>
            <div style={{ minWidth: 0 }}>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                Admin Colleges Dashboard
              </h1>
              <p style={{ margin: '2px 0 0', fontSize: 12.5, color: '#94A3B8', lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                Manage colleges, subscriptions &amp; credit utilisation
              </p>
            </div>
          </div>

          {/* Right: stat pills + refresh */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>

            {/* Total Colleges pill */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 16px', borderRadius: 10,
              background: '#EEF2FF', border: '1.5px solid #C7D2FE',
            }}>
              <div style={{ width: 28, height: 28, borderRadius: 7, background: 'rgba(99,102,241,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>
                🏛️
              </div>
              <div>
                <div style={{ fontSize: 17, fontWeight: 800, color: '#4338CA', lineHeight: 1 }}>{fmtNum(stats.total)}</div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#6366F1', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 2 }}>Total Colleges</div>
              </div>
            </div>

            {/* Active Subscriptions pill */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 16px', borderRadius: 10,
              background: '#ECFDF5', border: '1.5px solid #A7F3D0',
            }}>
              <div style={{ width: 28, height: 28, borderRadius: 7, background: 'rgba(16,185,129,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>
                ✅
              </div>
              <div>
                <div style={{ fontSize: 17, fontWeight: 800, color: '#065F46', lineHeight: 1 }}>{fmtNum(stats.active)}</div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#10B981', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 2 }}>Active Subscriptions</div>
              </div>
            </div>

            {/* Divider */}
            <div style={{ width: 1, height: 32, background: '#E2E8F0' }} />

            {/* Refresh button */}
            <button
              onClick={fetchColleges}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '9px 16px', borderRadius: 9, border: '1.5px solid #E2E8F0',
                background: '#fff', color: '#334155', fontSize: 13, fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#F1F5F9'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
            >
              🔄 Refresh
            </button>
          </div>
        </div>
      </div>

      {/* ── Filter Bar ── */}
      <div style={{
        background: '#fff', borderRadius: 12, padding: '16px 18px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid #E2E8F0',
        marginBottom: 20,
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          {[
            { val: searchName,  set: setSearchName,  ph: '🔍 College Name' },
            { val: searchEmail, set: setSearchEmail, ph: '📧 Email' },
            { val: searchUser,  set: setSearchUser,  ph: '👤 Username' },
            { val: searchPhone, set: setSearchPhone, ph: '📞 Phone' },
          ].map(({ val, set, ph }) => (
            <input key={ph} value={val} onChange={e => set(e.target.value)} placeholder={ph}
              style={{
                height: 40, padding: '0 12px', borderRadius: 8, fontSize: 13, fontWeight: 500,
                border: '1.5px solid #E2E8F0', color: '#334155', outline: 'none', minWidth: 150, flex: '1 1 150px',
              }}
            />
          ))}
          <FilterSelect value={filterPlan}   onChange={setFilterPlan}   options={allPlans}   placeholder="All Plans" />
          <FilterSelect value={filterState}  onChange={setFilterState}  options={allStates}  placeholder="All States" />
          <FilterSelect value={filterStatus} onChange={setFilterStatus} options={allStatuses} placeholder="All Statuses" />
          {hasFilter && (
            <button onClick={resetFilters} style={{
              height: 40, padding: '0 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              border: '1.5px solid #FCA5A5', background: '#FFF5F5', color: '#DC2626', cursor: 'pointer',
            }}>
              ✕ Reset
            </button>
          )}
          <span style={{ fontSize: 12, color: '#94A3B8', fontWeight: 600, marginLeft: 'auto' }}>
            {filtered.length} / {colleges.length} colleges
          </span>
        </div>
      </div>

      {/* ── Table ── */}
      <div style={{
        background: '#fff', borderRadius: 14, boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
        border: '1px solid #E2E8F0', overflow: 'hidden',
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1400 }}>

            {/* Sticky header */}
            <thead>
              <tr>
                <TH style={{ position: 'sticky', left: 0, zIndex: 2, background: '#F8FAFC' }}>#</TH>
                <TH style={{ position: 'sticky', left: 44, zIndex: 2, background: '#F8FAFC', minWidth: 180 }}>College Name</TH>
                <TH>State</TH>
                <TH>Country</TH>
                <TH>Email</TH>
                <TH>Phone</TH>
                <TH>Username</TH>
                <TH>Active Plan</TH>
                <TH>Subscription Date</TH>
                <TH>Valid Till</TH>
                <TH>Credits Purchased</TH>
                <TH>Credits Used</TH>
                <TH>Remaining Credits</TH>
                <TH>Status</TH>
                <TH style={{ position: 'sticky', right: 0, zIndex: 2, background: '#F8FAFC', textAlign: 'center' }}>Actions</TH>
              </tr>
            </thead>

            <tbody>
              {/* ── Skeleton rows ── */}
              {loading && Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                  {Array.from({ length: 15 }).map((__, j) => (
                    <td key={j} style={{ padding: '14px 14px', borderBottom: '1px solid #F1F5F9' }}>
                      <Skeleton w={j === 1 ? '80%' : j === 4 ? '90%' : '60%'} h={14} />
                    </td>
                  ))}
                </tr>
              ))}

              {/* ── Empty state ── */}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={15} style={{ padding: '64px 24px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                      <div style={{ fontSize: 52 }}>🏛️</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#1E293B' }}>No Colleges Found</div>
                      <div style={{ fontSize: 13, color: '#94A3B8', maxWidth: 320 }}>
                        {hasFilter
                          ? 'No college records match the selected filters.'
                          : 'No colleges have been registered yet.'}
                      </div>
                      {hasFilter && (
                        <button onClick={resetFilters} style={{
                          marginTop: 4, padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                          background: '#6366F1', color: '#fff', border: 'none', cursor: 'pointer',
                        }}>
                          Clear Filters
                        </button>
                      )}
                      {!hasFilter && (
                        <button onClick={fetchColleges} style={{
                          marginTop: 4, padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                          background: '#6366F1', color: '#fff', border: 'none', cursor: 'pointer',
                        }}>
                          🔄 Refresh
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}

              {/* ── Data rows ── */}
              {!loading && filtered.map((college, idx) => {
                const plan          = planLabel(college.active_plan_code);
                const planStyle     = PLAN_STYLE[plan] || PLAN_STYLE.Free;
                const statusObj     = getSubscriptionStatus(college.plan_valid_till, college.active_plan_code);
                const purchased     = Number(college.total_credits_purchased || 0);
                const used          = Number(college.credits_used || 0);
                const remaining     = Number(college.available_credits ?? Math.max(purchased - used, 0));
                const usedPct       = purchased > 0 ? Math.min((used / purchased) * 100, 100) : 0;
                const barColor      = creditBarColor(usedPct);
                const lowCredits    = purchased > 0 && remaining / purchased < 0.10;
                const isNearExpiry  = statusObj.label === 'Near Expiry';
                const isExpired     = statusObj.label === 'Expired';
                const rowBg         = idx % 2 === 0 ? '#fff' : '#FAFBFC';

                return (
                  <tr
                    key={college.id}
                    style={{ background: rowBg, transition: 'background 0.12s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#F1F5FF'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = rowBg; }}
                  >
                    {/* # */}
                    <TD style={{ position: 'sticky', left: 0, background: 'inherit', color: '#94A3B8', fontSize: 12, fontWeight: 600 }}>
                      {idx + 1}
                    </TD>

                    {/* College Name */}
                    <TD style={{ position: 'sticky', left: 44, background: 'inherit', fontWeight: 700, color: '#0F172A', minWidth: 180 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                          background: 'linear-gradient(135deg,#6366F1,#8B5CF6)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#fff', fontSize: 13, fontWeight: 800,
                        }}>
                          {(college.name || '?').charAt(0).toUpperCase()}
                        </div>
                        <span style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={college.name}>
                          {college.name}
                        </span>
                      </div>
                    </TD>

                    {/* State */}
                    <TD>{college.state || '—'}</TD>

                    {/* Country */}
                    <TD>{college.country || '—'}</TD>

                    {/* Email */}
                    <TD>
                      <span style={{ maxWidth: 190, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={college.email}>
                        {college.email || '—'}
                      </span>
                    </TD>

                    {/* Phone */}
                    <TD style={{ whiteSpace: 'nowrap' }}>{college.phone || '—'}</TD>

                    {/* Username */}
                    <TD style={{ fontFamily: 'monospace', fontSize: 12, color: '#475569' }}>{college.username}</TD>

                    {/* Active Plan */}
                    <TD>
                      <Badge label={plan} bg={planStyle.bg} color={planStyle.color} border={planStyle.border} />
                    </TD>

                    {/* Subscription Date */}
                    <TD style={{ whiteSpace: 'nowrap', color: '#64748B', fontSize: 12 }}>
                      {fmtDate(college.plan_activated_at)}
                    </TD>

                    {/* Valid Till */}
                    <TD style={{ whiteSpace: 'nowrap' }}>
                      {!college.plan_valid_till ? <span style={{ color: '#94A3B8' }}>—</span> : (
                        <div>
                          <div style={{ fontSize: 12, color: isExpired ? '#DC2626' : isNearExpiry ? '#D97706' : '#334155', fontWeight: 600 }}>
                            {fmtDate(college.plan_valid_till)}
                          </div>
                          {(isExpired || isNearExpiry) && (
                            <Badge
                              label={isExpired ? '⛔ Expired' : '⚠ Near Expiry'}
                              bg={isExpired ? '#FEE2E2' : '#FEF3C7'}
                              color={isExpired ? '#991B1B' : '#92400E'}
                              border={isExpired ? '#FECACA' : '#FDE68A'}
                            />
                          )}
                        </div>
                      )}
                    </TD>

                    {/* Credits Purchased */}
                    <TD>
                      <span style={{
                        display: 'inline-block', padding: '3px 10px', borderRadius: 6,
                        background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE',
                        fontSize: 12, fontWeight: 700,
                      }}>
                        {fmtNum(purchased)} Credits
                      </span>
                    </TD>

                    {/* Credits Used */}
                    <TD style={{ minWidth: 160 }}>
                      {purchased > 0 ? (
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontWeight: 600, color: '#64748B', marginBottom: 4 }}>
                            <span>{fmtNum(used)} / {fmtNum(purchased)}</span>
                            <span style={{ color: barColor, fontWeight: 700 }}>{Math.round(usedPct)}%</span>
                          </div>
                          <div style={{ height: 6, borderRadius: 4, background: '#F1F5F9', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${usedPct}%`, borderRadius: 4, background: barColor, transition: 'width 0.3s' }} />
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: '#94A3B8', fontSize: 12 }}>—</span>
                      )}
                    </TD>

                    {/* Remaining Credits */}
                    <TD>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <span style={{
                          fontSize: 13, fontWeight: 700,
                          color: remaining === 0 ? '#DC2626' : lowCredits ? '#D97706' : '#065F46',
                        }}>
                          {fmtNum(remaining)}
                        </span>
                        {lowCredits && (
                          <span title="Less than 10% credits remaining" style={{ fontSize: 14, cursor: 'help' }}>⚠️</span>
                        )}
                      </div>
                    </TD>

                    {/* Status */}
                    <TD>
                      <Badge
                        label={
                          statusObj.label === 'Active'     ? '🟢 Active' :
                          statusObj.label === 'Near Expiry' ? '🟠 Near Expiry' :
                          statusObj.label === 'Expired'    ? '🔴 Expired' : '⚫ Inactive'
                        }
                        bg={statusObj.style.bg}
                        color={statusObj.style.color}
                        border={statusObj.style.border}
                      />
                    </TD>

                    {/* Actions */}
                    <TD style={{ position: 'sticky', right: 0, background: 'inherit', textAlign: 'center' }}>
                      <button
                        onClick={() => handleOpenDialog(college.id)}
                        title="Delete college"
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 700,
                          background: '#FEF2F2', color: '#DC2626', border: '1.5px solid #FECACA',
                          cursor: 'pointer', transition: 'all 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#DC2626'; e.currentTarget.style.color = '#fff'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#FEF2F2'; e.currentTarget.style.color = '#DC2626'; }}
                      >
                        🗑 Delete
                      </button>
                    </TD>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer row count */}
        {!loading && filtered.length > 0 && (
          <div style={{ padding: '12px 18px', borderTop: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: '#94A3B8', fontWeight: 600 }}>
              Showing {filtered.length} of {colleges.length} colleges
            </span>
            <span style={{ fontSize: 12, color: '#CBD5E1', fontWeight: 500 }}>
              Last refreshed: {new Date().toLocaleTimeString('en-IN')}
            </span>
          </div>
        )}
      </div>

      {/* ── Delete Confirmation Dialog (preserved exactly) ── */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        PaperProps={{
          sx: {
            backgroundColor: '#FFFFFF',
            borderRadius: 4,
            border: '1px solid var(--accent-gray)',
            color: 'var(--text-body)',
          },
        }}
      >
        <DialogTitle sx={{ color: 'var(--primary-blue)', fontWeight: 'bold' }}>Confirm Deletion</DialogTitle>
        <DialogContent sx={{ color: 'var(--text-body)' }}>
          Are you sure you want to delete this college? This action cannot be undone.
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDialog} sx={{ color: 'var(--text-muted)', fontWeight: 'bold' }} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            disabled={deleting}
            sx={{ backgroundColor: '#ef4444', color: '#fff', fontWeight: 'bold', borderRadius: 2, '&:hover': { backgroundColor: '#dc2626' } }}
          >
            {deleting ? 'Deleting…' : 'Confirm Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AdminCollegesDashboard;
